import to from 'await-to-js';
import { Component, ContentType, PagedList, Project } from 'contensis-core-api';
import { TagGroup } from 'contensis-management-api';
import deepCleaner from 'deep-cleaner';
import {
  IContensisContext,
  ModelDependencies,
  MigrateRequest,
  Model,
} from '~/models';
import ContensisRepository from '~/repositories/ContensisRepository';
import { dedupeInner } from '~/util/array';
import ContentTypeHelper from '~/util/content-type/ContentTypeHelper';
import { jsonIsEqual } from '~/util/diff';
import { logError, Logger, newProgressBar } from '~/util/logger';
import { isArray } from '~/util/typeguards';

const arraySort = (a: (string | string[])[], b: (string | string[])[]) => {
  const vala = (Array.isArray(a) ? a?.[0] : a) as string;
  const valb = (Array.isArray(b) ? b?.[0] : b) as string;
  if (vala < valb) return -1;
  if (vala > valb) return 1;
};

class ModelGenerationService {
  private context!: IContensisContext;
  private noCache?: boolean;
  private outputProgress?: boolean;
  private raw: Model[] = [];

  constructor(
    private contensis: ContensisRepository,
    private payload: MigrateRequest,
    private isSource = false
  ) {
    this.noCache = payload.noCache;
    this.outputProgress = payload.outputProgress;
  }

  // When we push dependencies we will reference the
  // object:array inside contentTypeHelper which will become
  // ContensisRepository.context
  get dependencies() {
    return this.context.dependencies;
  }
  get contentTypes() {
    return this.context.contentTypes;
  }
  set contentTypes(val) {
    this.context.contentTypes = val;
  }
  get components() {
    return this.context.components;
  }
  set components(val) {
    this.context.components = val;
  }
  get tagGroups() {
    return this.context.tagGroups;
  }
  set tagGroups(val) {
    this.context.tagGroups = val;
  }

  get models() {
    return this.raw;
  }

  /**
   * Get Contensis context to provide a handle on anything we have fetched from the repository during hydration
   *  * project
   *  * contentTypes
   *  * components
   *  * taggroups
   *  * entries (resolved from dependencies)
   *  * models (built from dependencies)
   *  * nodes (resolved from dependencies)
   *  * dependencies
   * @param assets work with any content types or components added via the payload
   */
  GetContensisContext = async (
    resourceIds?: string[],
    assets?: (ContentType | Component)[]
  ): Promise<IContensisContext> => {
    const { repo } = this.contensis;
    const contentTypesCached = repo?.cache.getFile('_contenttypes');
    const componentsCached = repo?.cache.getFile('_components');
    const tagGroupsCached = repo?.cache.getFile('_taggroups');

    let project: Project | undefined;
    // If we have an array of assets supplied with the payload...
    if (Array.isArray(assets)) {
      const contentTypes: ContentType[] = [];
      const components: Component[] = [];
      for (const resource of assets)
        if (ContentTypeHelper.assert.isContentType(resource))
          contentTypes.push(resource);
        else components.push(resource);
      this.context = new ContentTypeHelper(
        contentTypes,
        components,
        (this.payload.tagGroups || []) as TagGroup[]
      ) as IContensisContext;
    } else {
      // Get all content types and components so we can get a complete picture
      // of the project
      project = await repo.GetProject();
      const promises: [
        Promise<[Error, undefined] | [null, ContentType[]]>,
        Promise<[Error, undefined] | [null, Component[]]>,
        Promise<[Error, undefined] | [null, PagedList<TagGroup>]>,
      ] = [
        (async () => {
          const [err, contentTypes] = await to(repo.GetContentTypes());
          if (err) logError(err, 'Error fetching content types');
          return [err, contentTypes] as
            | [Error, undefined]
            | [null, ContentType[]];
        })(),
        (async () => {
          const [err, components] = await to(repo.GetComponents());
          if (err) logError(err, 'Error fetching components');
          return [err, components] as [Error, undefined] | [null, Component[]];
        })(),
        repo.GetTagGroups({ pageOptions: { pageSize: 300 } }),
      ];
      const resolved = await Promise.all(promises);

      const [, contentTypes] = resolved[0];
      const [, components] = resolved[1];
      const [err, { items: tagGroups = [] } = {}] = resolved[2];
      if (err) logError(err, 'Error fetching tag groups');

      if (contentTypes && components)
        this.context = new ContentTypeHelper(
          contentTypes,
          components,
          tagGroups,
          project
        ) as IContensisContext;
      else
        // Perhaps we should abort the process if we can't get content types/components
        throw new Error('Could not fetch content types or components');
    }
    this.context.payload = this.payload;

    // Sort returned content types, components and tag groups by their id
    this.contentTypes = this.contentTypes.sort((a, b) =>
      a.id < b.id ? -1 : a.id > b.id ? 1 : 0
    );
    this.components = this.components.sort((a, b) =>
      a.id < b.id ? -1 : a.id > b.id ? 1 : 0
    );
    this.tagGroups = this.tagGroups.sort((a, b) =>
      a.id < b.id ? -1 : a.id > b.id ? 1 : 0
    );

    const unchanged =
      this.noCache === true
        ? false
        : jsonIsEqual(contentTypesCached, this.contentTypes) &&
          jsonIsEqual(componentsCached, this.components) &&
          jsonIsEqual(tagGroupsCached, this.tagGroups);

    if (this.contentTypes)
      repo?.cache.writeFile('_contenttypes', this.contentTypes);
    if (this.components) repo?.cache.writeFile('_components', this.components);
    if (this.tagGroups) repo?.cache.writeFile('_taggroups', this.tagGroups);

    // Generate dependency/dependencyOf lists for each component and content type
    this.GenerateDependenciesList();

    // Resolve default entries and nodes
    if (this.payload.includeDefaults) {
      await this.ResolveDefaults(resourceIds);

      // Reconcile content dependencies after defaults have been resolved
      this.GenerateEntryDependenciesList();
    }

    // We do not have a source repo if assets are supplied in payload
    if (repo)
      this.raw = await repo.CacheRequest(
        '_models',
        this.GenerateModels,
        !unchanged
      );
    else this.raw = await this.GenerateModels();

    // Load up contentTypeHelper to become IContensisContext
    this.context.models = this.raw;

    return this.context as IContensisContext;
  };

  private GenerateDependenciesList = () => {
    const { context } = this;
    Logger.info(
      `Finding all dependencies for ${
        this.contentTypes.length
      } content types and ${this.components.length} components for ${
        this.isSource ? 'source' : 'target'
      } project ${this.contensis.projectId} in ${this.contensis.alias}`,
      this.contentTypes.length >= 30
    );

    const entryIds = new Set<string>();
    const nodeIds = new Set<string>();
    const tagGroupIds = new Set<string>();

    const populateContentDependencies = ({
      defaults,
      nodes,
      tagGroups,
    }: ModelDependencies['dependencies']) => {
      for (const [nodeId] of nodes)
        if (!nodeIds.has(nodeId)) {
          nodeIds.add(nodeId);
          this.dependencies.nodes.push({
            id: nodeId,
            dependencies: undefined as any,
            dependencyOf: { contentTypes: [], components: [] },
          });
        }

      for (const [entryId] of defaults)
        if (!entryIds.has(entryId)) {
          entryIds.add(entryId);
          this.dependencies.defaults.push({
            id: entryId,
            // Haven't resolved defaults yet so can't find dependencies
            dependencies: undefined as any,
            dependencyOf: { contentTypes: [], components: [] },
          });
        }
      for (const tagGroupId of tagGroups)
        if (!tagGroupIds.has(tagGroupId)) {
          tagGroupIds.add(tagGroupId);
          this.dependencies.tagGroups.push({
            id: tagGroupId,
            dependencies: undefined as any,
            dependencyOf: { contentTypes: [], components: [] },
          });
        }
    };

    // Find all dependencies for every content type
    for (const contentType of this.contentTypes) {
      const dependencies = context.resolve.dependencies(contentType);
      this.dependencies.contentTypes.push({
        id: contentType.id,
        dependencies,
        dependencyOf: { contentTypes: [], components: [] },
      });
      populateContentDependencies(dependencies);
    }

    // Find all dependencies for every component
    for (const component of this.components) {
      const dependencies = context.resolve.dependencies(component);
      this.dependencies.components.push({
        id: component.id,
        dependencies,
        dependencyOf: { contentTypes: [], components: [] },
      });
      populateContentDependencies(dependencies);
    }

    // Populate `dependencyOf` in each dependency type in this.dependencies
    // Iterate through generated dependencies to find any content type
    // or component ids that the resource is a dependent of
    for (const modelAsset of this.dependencies.contentTypes) {
      modelAsset.dependencyOf.contentTypes?.push(
        ...this.dependencies.contentTypes
          .filter((d) => d.dependencies.contentTypes.includes(modelAsset.id))
          .map((d) => d.id)
      );
      modelAsset.dependencyOf.components?.push(
        ...this.dependencies.components
          .filter((d) => d.dependencies.contentTypes.includes(modelAsset.id))
          .map((d) => d.id)
      );
    }
    for (const modelAsset of this.dependencies.components) {
      modelAsset.dependencyOf.contentTypes?.push(
        ...this.dependencies.contentTypes
          .filter((d) => d.dependencies.components.includes(modelAsset.id))
          .map((d) => d.id)
      );
      modelAsset.dependencyOf.components?.push(
        ...this.dependencies.components
          .filter((d) => d.dependencies.components.includes(modelAsset.id))
          .map((d) => d.id)
      );
    }
    for (const modelAsset of this.dependencies.defaults) {
      modelAsset.dependencyOf.contentTypes?.push(
        ...this.dependencies.contentTypes
          .filter((d) =>
            d.dependencies.defaults.find((e) => e[0] === modelAsset.id)
          )
          .map((d) => d.id)
      );
      modelAsset.dependencyOf.components?.push(
        ...this.dependencies.components
          .filter((d) =>
            d.dependencies.defaults.find((e) => e[0] === modelAsset.id)
          )
          .map((d) => d.id)
      );
    }
    for (const modelAsset of this.dependencies.nodes) {
      modelAsset.dependencyOf.contentTypes?.push(
        ...this.dependencies.contentTypes
          .filter((d) =>
            d.dependencies.nodes.find((n) => n[0] === modelAsset.id)
          )
          .map((d) => d.id)
      );
      modelAsset.dependencyOf.components?.push(
        ...this.dependencies.components
          .filter((d) =>
            d.dependencies.nodes.find((n) => n[0] === modelAsset.id)
          )
          .map((d) => d.id)
      );
    }
    for (const modelAsset of this.dependencies.tagGroups) {
      modelAsset.dependencyOf.contentTypes?.push(
        ...this.dependencies.contentTypes
          .filter((d) =>
            d.dependencies.tagGroups.find((g) => g === modelAsset.id)
          )
          .map((d) => d.id)
      );
      modelAsset.dependencyOf.components?.push(
        ...this.dependencies.components
          .filter((d) =>
            d.dependencies.tagGroups.find((g) => g === modelAsset.id)
          )
          .map((d) => d.id)
      );
    }

    // remove empty arrays and keys from the lists
    deepCleaner(this.dependencies.contentTypes);
    deepCleaner(this.dependencies.components);
  };

  private ResolveDefaults = async (resourceIds?: string[]) => {
    // Get all dependent nodes and entries
    const entryIds = new Set<string>();
    const nodeIds = new Map<string, string>();
    for (const contentType of this.dependencies.contentTypes) {
      if (
        !resourceIds ||
        (isArray(resourceIds) && resourceIds.length === 0) ||
        resourceIds.find((id) => contentType.id === id)
      ) {
        for (const [entryId] of contentType.dependencies.defaults || [])
          entryIds.add(entryId);
        for (const [nodeId, nodePath] of contentType.dependencies.nodes || [])
          nodeIds.set(nodeId, nodePath || '');
      }
    }
    for (const component of this.dependencies.components) {
      if (
        !resourceIds ||
        (isArray(resourceIds) && resourceIds.length === 0) ||
        resourceIds.find((id) => component.id === id)
      ) {
        for (const [entryId] of component.dependencies.defaults || [])
          entryIds.add(entryId);
        for (const [nodeId, nodePath] of component.dependencies.nodes || [])
          nodeIds.set(nodeId, nodePath || '');
      }
    }
    if (entryIds.size > 0) {
      // We are so early in the migration process this repository entries class
      // will be replaced with a fresh instantiation
      // TODO: check if we can avoid reinstantiating services in the source repo
      // after models are generated
      this.contensis.context = this.context; // Set the Contensis context manually (so we can find dependencies) as it isn't fully prepared at this stage
      await to(
        this.contensis.entries.GetDependentEntries(
          [...entryIds],
          this.contentTypes.length,
          'content types',
          0,
          1
        )
      );
    }
    if (nodeIds.size > 0) {
      const [err] = await to(
        this.contensis.entries.GetDependentNodes(nodeIds, 1, 'models')
      );
      if (err) logError(err, 'Error fetching dependent nodes for models');
    }

    // Load up contentTypeHelper to become IContensisContext
    this.context.entries = this.contensis.entries.raw;
    this.context.nodes = this.contensis.nodes.raw;
  };

  private GenerateEntryDependenciesList = () => {
    const { contensis } = this;
    Logger.info(
      `Finding dependencies for ${
        this.dependencies.defaults.length
      } default entries for ${this.isSource ? 'source' : 'target'} project ${
        this.contensis.projectId
      } in ${this.contensis.alias}`,
      this.contentTypes.length >= 30
    );

    // Find all dependencies for every default entry
    for (const defaults of this.dependencies.defaults) {
      const entry = contensis.context.entries.find(
        (e) => e.sys.id === defaults.id
      );
      if (entry)
        defaults.dependencies =
          contensis.entries.dependencies.FindEntryDependencies(entry);
    }
  };

  private GenerateModels = async () => {
    const requiresProgress =
      this.outputProgress && !!(this.contentTypes.length > 50);

    // Generate models for each content type
    Logger.info(
      `Generating ${this.contentTypes.length} content models for ${
        this.isSource ? 'source' : 'target'
      } project ${this.contensis.projectId} in ${this.contensis.alias}`,
      requiresProgress
    );
    const models = [];
    const pr = newProgressBar(
      ` :percent ${Logger.infoText(':elapseds/:etas')} :contentTypeId`,
      {
        output: requiresProgress,
        total: this.contentTypes.length || 0,
      }
    );

    for (const contentType of this.contentTypes) {
      pr?.tick({ contentTypeId: contentType.id });
      models.push(await this.GenerateModel(contentType));
    }
    return models;
  };

  private GenerateModel = async (contentType: ContentType) => {
    const {
      dependencies = {
        contentTypes: [],
        components: [],
        defaults: [],
        nodes: [],
        tagGroups: [],
      },
      dependencyOf,
    } = this.dependencies.getById(contentType.id) || {};
    const { nodes, resolve } = this.context;

    const contentTypes =
      dependencies.contentTypes
        ?.sort()
        .map<[string, string]>((id) => [id, contentType.id]) || [];
    const components =
      dependencies.components
        ?.sort()
        .map<
          [string, string]
        >((id) => [resolve.componentId(id), contentType.id]) || [];
    const tagGroups =
      dependencies.tagGroups
        ?.sort()
        .map<[string, string]>((id) => [id, contentType.id]) || [];

    const contentTypeIdList = contentTypes.map(([id]) => id);
    const componentIdList = components.map(([id]) => id);
    const tagGroupIdList = tagGroups.map(([id]) => id);
    const listOfAllIds = new Set([...contentTypeIdList, ...componentIdList]);

    const model: Model = {
      id: contentType.id,
      dataFormat: 'model',
      name: contentType.name,
      description: contentType.description,
      // Content types and components directly attached to this content type
      contentTypes: contentTypeIdList,
      components: componentIdList,
      defaults: dependencies.defaults,
      nodes: dependencies.nodes?.map((n) => [
        n[0],
        nodes.find((node) => node.id === n[0])?.path || '',
      ]),
      tagGroups: tagGroupIdList,
      // List of content type and component ids that this model is a dependency of
      dependencyOf,
      // Complete list of content types and components including all dependencies
      // of any dependencies, supplemented with any other ids that are a dependency of that same dependency
      dependencies: {
        contentTypes: [],
        components: [],
        tagGroups: [],
      },
    };

    // find content type and component dependencies inside
    // the already found content types and components
    let prevCount = -1;
    const checkedIds = new Set<string>();
    while (listOfAllIds.size !== prevCount) {
      // We can stop looping after the content types array has no more
      // added since the previous iteration
      prevCount = listOfAllIds.size;

      for (const id of listOfAllIds) {
        if (checkedIds.has(id)) continue;
        // Match each content type inside the model against the
        // project's content types and then find the dependencies of those
        const modelAsset = resolve.resource(id);

        if (modelAsset) {
          const { dependencies: modelAssetDependencies } =
            this.dependencies.getById(modelAsset.id) ||
            ({} as ModelDependencies);

          // add any content types from the found dependencies for this model asset to the
          // list of all ids
          (modelAssetDependencies.contentTypes || [])
            .concat(modelAssetDependencies.components || [])
            .forEach((id) => listOfAllIds.add(id));

          // add the contentType id and parent id from any found dependency content types to
          // the list of dependencies for the current model we are generating
          for (const dct of modelAssetDependencies.contentTypes || []) {
            if (!contentTypes.find(([id]) => id === dct[0]))
              contentTypes.push([dct, modelAsset.id]);
          }

          for (const dct of modelAssetDependencies.components || []) {
            if (!components.find(([id]) => id === dct[0]))
              components.push([resolve.componentId(dct), modelAsset.id]);
          }

          for (const dtg of modelAssetDependencies.tagGroups || []) {
            if (!tagGroups.find(([id]) => id === dtg[0]))
              tagGroups.push([resolve.tagGroupId(dtg), modelAsset.id]);
          }
        }

        checkedIds.add(id);
      }
    }

    if (model.dependencies) {
      // This goes through the arrays to create a complete list of dependencies
      // that can be indirect dependents of the model we are generating,
      // the second array item provides a complete list of ids that the current id is a dependency of
      model.dependencies.contentTypes = dedupeInner(
        contentTypes.map(([ict], idx, arr) => [
          ict,
          arr
            .filter(([aict, aiparent]) => aict === ict) // scope to current content type
            .map(([, aiparent]) => aiparent), // map list of ids that require this content type,
        ])
      ).sort(arraySort as any);
      model.dependencies.components = dedupeInner(
        components
          .map(([ict], idx, arr) => [
            ict,
            arr
              .filter(([aict, aiparent]) => aict === ict)
              .map(([, aiparent]) => aiparent),
          ])
          .sort(arraySort as any)
      );
      model.dependencies.tagGroups = dedupeInner(
        tagGroups
          .map(([ict], idx, arr) => [
            ict,
            arr
              .filter(([aict, aiparent]) => aict === ict)
              .map(([, aiparent]) => aiparent),
          ])
          .sort(arraySort as any)
      );
    }

    return model;
  };
}
export default ModelGenerationService;
